import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

interface CrearCanonicoModalProps {
  open: boolean;
  nombreSugerido?: string;
  onClose: () => void;
  onConfirmar: (nombre: string, tags: string[], subcategoriaId?: number) => Promise<void>;
  loading?: boolean;
}

export function CrearCanonicoModal({
  open,
  nombreSugerido = '',
  onClose,
  onConfirmar,
  loading = false,
}: CrearCanonicoModalProps) {
  const [nombre, setNombre] = useState(nombreSugerido);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setNombre(nombreSugerido);
      setTags([]);
      setTagInput('');
      setError(null);
    }
  }, [open, nombreSugerido]);

  const agregarTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
      setTagInput('');
    }
  };

  const eliminarTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleConfirmar = async () => {
    if (!nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }
    setError(null);
    try {
      await onConfirmar(nombre.trim(), tags);
    } catch (err: any) {
      setError(err.message || 'Error al crear canónico');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f7edf5' }}>
        <Typography variant="h6" fontWeight="bold" color="#3d1e3d">
          Crear producto canónico
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Nombre del producto canónico"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          fullWidth
          required
          helperText="Nombre genérico del concepto (ej: Pelota antiestrés)"
        />

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sinónimos / Tags (opcional)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              placeholder="Agregar sinónimo..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && agregarTag()}
              sx={{ flexGrow: 1 }}
            />
            <Button variant="outlined" size="small" onClick={agregarTag} startIcon={<AddIcon />}>
              Agregar
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => eliminarTag(tag)}
                sx={{ backgroundColor: '#e8d8e8', color: '#3d1e3d' }}
              />
            ))}
          </Box>
        </Box>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          disabled={loading || !nombre.trim()}
          sx={{ backgroundColor: '#3d1e3d', '&:hover': { backgroundColor: '#5a2e5a' } }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Crear y mapear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
